import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { Loader2 } from "lucide-react";

import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogDescription,
	DialogFooter,
} from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { cn } from "../../lib/utils";

import api from "../../services/api";
import toastError from "../../errors/toastError";

// Cores fixas em hex de propósito: é a cor escolhida pela pessoa para a
// coluna dela, não um token do tema.
const CORES = [
	"#3b82f6",
	"#22c55e",
	"#f59e0b",
	"#ef4444",
	"#a855f7",
	"#06b6d4",
	"#64748b",
];

const ColunaModal = ({ open, coluna, onClose, onSaved }) => {
	const [name, setName] = useState("");
	const [color, setColor] = useState(CORES[0]);
	const [triggerMessage, setTriggerMessage] = useState("");
	const [salvando, setSalvando] = useState(false);
	// Sem conexão de pé o aviso não sai, e a pessoa só descobriria arrastando
	// o cartão e não vendo nada acontecer.
	const [semConexao, setSemConexao] = useState(false);

	const editando = !!coluna?.id;

	useEffect(() => {
		if (!open) return;

		(async () => {
			try {
				const { data } = await api.get("/whatsapp/");
				const conectadas = (Array.isArray(data) ? data : []).filter(
					(c) => c.status === "CONNECTED",
				);
				setSemConexao(conectadas.length === 0);
			} catch {
				setSemConexao(false);
			}
		})();
	}, [open]);

	useEffect(() => {
		if (!open) return;
		setName(coluna?.name || "");
		setColor(coluna?.color || CORES[0]);
		setTriggerMessage(coluna?.triggerMessage || "");
	}, [open, coluna]);

	const salvar = async () => {
		if (!name.trim()) {
			toast.error("Dê um nome à coluna.");
			return;
		}

		setSalvando(true);
		try {
			const corpo = { name, color, triggerMessage };

			if (editando) {
				await api.put(`/kanban/columns/${coluna.id}`, corpo);
			} else {
				await api.post("/kanban/columns", corpo);
			}

			toast.success(editando ? "Coluna salva." : "Coluna criada.");
			onSaved?.();
			onClose();
		} catch (err) {
			toastError(err);
		}
		setSalvando(false);
	};

	return (
		<Dialog open={open} onOpenChange={(o) => !o && onClose()}>
			<DialogContent className="sm:max-w-lg">
				<DialogHeader>
					<DialogTitle>{editando ? "Editar coluna" : "Nova coluna"}</DialogTitle>
					<DialogDescription>
						Uma etapa do seu trabalho. O cliente pode ser avisado sozinho
						quando o cartão dele chegar aqui.
					</DialogDescription>
				</DialogHeader>

				<div className="space-y-4">
					<div className="space-y-1.5">
						<Label htmlFor="coluna-nome">Nome</Label>
						<Input
							id="coluna-nome"
							value={name}
							onChange={(e) => setName(e.target.value)}
							placeholder="Ex.: Em produção"
							autoFocus
						/>
					</div>

					<div className="space-y-1.5">
						<Label>Cor</Label>
						<div className="flex flex-wrap gap-2">
							{CORES.map((c) => (
								<button
									key={c}
									type="button"
									aria-label={`Cor ${c}`}
									onClick={() => setColor(c)}
									style={{ backgroundColor: c }}
									className={cn(
										"h-7 w-7 rounded-full border-2 transition-transform",
										color === c
											? "border-foreground scale-110"
											: "border-transparent",
									)}
								/>
							))}
						</div>
					</div>

					<div className="space-y-1.5">
						<Label htmlFor="coluna-aviso">
							Aviso ao cliente{" "}
							<span className="font-normal text-muted-foreground">
								(opcional)
							</span>
						</Label>
						<Textarea
							id="coluna-aviso"
							rows={3}
							value={triggerMessage}
							onChange={(e) => setTriggerMessage(e.target.value)}
							placeholder="Ex.: Olá {{name}}, seu pedido entrou em produção!"
						/>
						{semConexao && triggerMessage.trim() && (
							<p className="text-xs leading-relaxed text-destructive">
								Nenhuma conexão do WhatsApp está conectada agora. O aviso fica
								guardado, mas só começa a sair quando uma conexão voltar.
							</p>
						)}
						<p className="text-xs leading-relaxed text-muted-foreground">
							Sai pela conexão em que o cliente já conversa assim que o cartão
							chega nesta coluna, e
							fica gravado na conversa para a equipe ver. Deixe em branco se
							esta etapa é só organização interna. Use{" "}
							<span className="font-medium text-foreground">{"{{name}}"}</span>{" "}
							para o nome do cliente.
						</p>
					</div>
				</div>

				<DialogFooter>
					<Button variant="outline" onClick={onClose} disabled={salvando}>
						Cancelar
					</Button>
					<Button onClick={salvar} disabled={salvando}>
						{salvando && <Loader2 className="h-4 w-4 animate-spin" />}
						{editando ? "Salvar" : "Criar coluna"}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
};

export default ColunaModal;
